import { useEffect, useRef, useState } from 'react';
import './AboutUsPage.css';
import headOfSchoolPhoto from './assets/dummy.webp';
import aboutHero1 from './assets/news/news1.jpg';
import aboutHero2 from './assets/news/news5.jpg';
import aboutHero3 from './assets/news/news8.jpg';

function AboutUsPage({ page }) {
  const heroSlides = page?.hero_slides || [aboutHero1, aboutHero2, aboutHero3];
  const [activeHero, setActiveHero] = useState(0);
  const [selectedValue, setSelectedValue] = useState(null);
  const [animateWelcome, setAnimateWelcome] = useState(false);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setActiveHero((current) => (current + 1) % heroSlides.length);
    }, 6000);

    return () => clearInterval(intervalId);
  }, [heroSlides.length]);

  // Intersection Observer untuk Welcome Message
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setAnimateWelcome(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    const welcomeElement = document.getElementById('welcome-message');
    if (welcomeElement) {
      observer.observe(welcomeElement);
    }

    return () => observer.disconnect();
  }, []);

  const historyTimeline = page?.history_timeline || [
    {
      year: '2012',
      title: 'Primary Campus Opens',
      description: 'Permata Harapan School opens its first primary campus with a focus on faith, values, and academic excellence.',
      image: aboutHero1,
    },
    {
      year: '2021',
      title: 'Elementary Programme Launch',
      description: 'The school expands to serve growing families with a strengthened elementary learning pathway.',
      image: aboutHero2,
    },
    {
      year: '2026',
      title: 'Junior High Programme',
      description: 'SMP opens with enriched academics and leadership formation for early adolescents.',
      image: aboutHero3,
    },
    {
      year: '2027',
      title: 'Senior High Programme',
      description: 'SMA launches to prepare students for university pathways and global opportunities.',
      image: aboutHero1,
    },
  ];

  const [activeHistoryIndex, setActiveHistoryIndex] = useState(0);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setActiveHistoryIndex((current) => (current + 1) % historyTimeline.length);
    }, 6000);
    return () => clearInterval(intervalId);
  }, [historyTimeline.length]);

  const profile = {
    name: page?.principal_name || 'Dr. Amelia Hartono',
    credentials: page?.principal_credentials || 'M.Ed., Ed.D.',
    role: page?.principal_role || 'Principal / Head of School',
    opening: page?.principal_opening || 'Dear Parents, Students, and Members of the Permata Harapan Community,',
    paragraphOne:
      page?.principal_paragraph_one ||
      'Welcome to Permata Harapan School. Our mission is to nurture young people of character, competence, and compassion. We believe meaningful education combines academic rigor, strong values, and authentic care for every learner.',
    paragraphTwo:
      page?.principal_paragraph_two ||
      'Together with families, we build a learning environment where students are encouraged to think critically, lead responsibly, and serve others with integrity. We are committed to preparing each child not only for examinations, but for life and purpose.',
    closing:
      page?.principal_closing ||
      'Thank you for your trust and partnership. We look forward to walking this journey with you.',
    themeLine:
      page?.principal_theme_line ||
      'Nurturing Character, Inspiring Excellence, Building Futures.',
  };

  const leadershipProfiles = page?.leadership_profiles || [
    {
      name: 'Dr. Amelia Hartono',
      role: 'Principal / Head of School',
      bio: 'Leads the school vision, learning culture, and long-term academic growth with a focus on faith-centered excellence.',
      photo: headOfSchoolPhoto,
    },
    {
      name: 'Mrs. Clara Wijaya',
      role: 'Vice Principal, Academic Affairs',
      bio: 'Oversees curriculum quality, teacher development, and student achievement across every grade level.',
      photo: headOfSchoolPhoto,
    },
    {
      name: 'Mr. Daniel Prasetyo',
      role: 'Head of Student Life',
      bio: 'Guides character formation, leadership programs, and student well-being initiatives.',
      photo: headOfSchoolPhoto,
    },
    {
      name: 'Mrs. Rina Santoso',
      role: 'Head of Operations',
      bio: 'Ensures daily operations, campus safety, and family services run smoothly for the school community.',
      photo: headOfSchoolPhoto,
    },
  ];

  const philosophyItems = page?.school_philosophy || [
    {
      key: 'motto',
      label: 'School Motto',
      shortTitle: 'Non Scholae Sed Vitae Discimus',
      title: 'Non Scholae Sed Vitae Discimus',
      description:
        'The true purpose of education extends beyond academic achievement or the completion of schooling it is to cultivate the knowledge, skills, and character essential for meaningful success in life.',
      quote: 'Non Scholae Sed Vitae Discimus',
      index: '01',
      image: aboutHero1,
    },
    {
      key: 'philosophy',
      label: 'Philosophy',
      shortTitle: 'Faith, Fortitude, Fulfilment',
      title: 'Faith, Fortitude, and Fulfilment',
      points: [
        {
          title: 'Faith',
          description:
            'Faith serves as the foundational pillar of student character and development. It cultivates core values that shape how students think, act, and relate to others — encompassing spiritual grounding, moral and ethical guidance, and a genuine commitment to respect and tolerance.',
        },
        {
          title: 'Fortitude',
          description:
            'Fortitude embodies the strength, courage, and perseverance required to face and overcome adversity. It enables students to remain steadfast in the face of challenges, stay committed to their goals, and grow through difficulties with resilience and determination.',
        },
        {
          title: 'Fulfilment',
          description:
            'Fulfilment represents the sense of purpose and accomplishment that comes from personal growth and meaningful service to others. It is realised when students develop their full potential while contributing positively to the communities they are part of.',
        },
      ],
      quote:
        'Together, Faith, Fortitude, and Fulfilment reflect the holistic qualities that Permata Harapan Secondary seeks to develop in every student. These core values are expressed through a set of guiding dispositions that shape students\' character, mindset, and actions.',
      index: '02',
      image: aboutHero2,
    },
    {
      key: 'logo',
      label: 'Logo Philosophy',
      shortTitle: 'Five Elements of Excellence',
      title: 'Logo Philosophy',
      points: [
        {
          title: 'Five-Leaved Tree',
          description:
            'A symbol of growth at Permata Harapan Secondary School. Each leaf reflects the school\'s commitment to knowledge, character, leadership, creativity, and global awareness.',
        },
        {
          title: 'Two Students in Graduation Gowns',
          description:
            'Represent Permata Harapan students on their journey to success. Standing side by side, they show unity, mutual support, and shared achievement within the school community.',
        },
        {
          title: 'Star',
          description:
            'A symbol that inspires Permata Harapan students to dream big and aim high. It represents excellence, hope, and future aspirations nurtured by the school.',
        },
        {
          title: 'Circle',
          description:
            'Represents balance, harmony, and unity in the Permata Harapan learning environment, inspired by the concept of Yin and Yang.',
        },
        {
          title: 'Ribbon',
          description:
            'A symbol of honor and celebration, reflecting Permata Harapan Secondary School\'s pride in every student\'s effort and achievement.',
        },
      ],
      index: '03',
      image: aboutHero3,
    },
  ];
  const [activePhilosophy, setActivePhilosophy] = useState(0);
  const [isAutoPlay, setIsAutoPlay] = useState(true);
  const touchStartXRef = useRef(null);

  useEffect(() => {
    if (!isAutoPlay) return;

    const intervalId = setInterval(() => {
      setActivePhilosophy((current) => (current + 1) % philosophyItems.length);
    }, 10000);

    return () => clearInterval(intervalId);
  }, [isAutoPlay, philosophyItems.length]);

  const goToPreviousPhilosophy = () => {
    setActivePhilosophy((current) =>
      (current - 1 + philosophyItems.length) % philosophyItems.length
    );
    setIsAutoPlay(false);
  };

  const goToNextPhilosophy = () => {
    setActivePhilosophy((current) => (current + 1) % philosophyItems.length);
    setIsAutoPlay(false);
  };

  const handlePhilosophyTouchStart = (event) => {
    touchStartXRef.current = event.touches?.[0]?.clientX ?? null;
  };

  const handlePhilosophyTouchEnd = (event) => {
    if (touchStartXRef.current === null) return;
    const endX = event.changedTouches?.[0]?.clientX ?? touchStartXRef.current;
    const deltaX = endX - touchStartXRef.current;
    const threshold = 50;
    if (Math.abs(deltaX) >= threshold) {
      if (deltaX > 0) {
        goToPreviousPhilosophy();
      } else {
        goToNextPhilosophy();
      }
    }
    touchStartXRef.current = null;
  };

  const valuesSection = {
    kicker: page?.values_kicker || 'Our Direction',
    title: page?.values_title || 'Vision, Mission, and Core Values',
    intro:
      page?.values_intro ||
      'These guiding principles shape how we teach, lead, and care for every student at Permata Harapan School.',
    cards: [
      {
        key: 'vision',
        label: page?.vision_label || 'Vision',
        title:
          page?.vision_title ||
          'To be inspired by faith, to be strengthened with fortitude, and to be motivated to achieve fulfillment through lifelong learning',
        description: page?.vision_description || 'Our vision guides every decision we make, ensuring that faith, courage, and purpose are at the heart of our educational journey.',
        image: aboutHero1,
      },
      {
        key: 'mission',
        label: page?.mission_label || 'Mission',
        title:
          page?.mission_title ||
          'PHB Secondary School is dedicated to empower students through a values-driven education, nurturing each student\'s spiritual, intellectual, and personal growth, inspiring them to face challenges with resilience, purpose, and a commitment to excellence.',
        description: page?.mission_description || 'We empower every student to become confident learners, compassionate leaders, and responsible global citizens through rigorous academics and character formation.',
        image: aboutHero2,
      },
      {
        key: 'core-values',
        label: page?.core_values_label || 'Core Values',
        title:
          page?.core_values_title ||
          'Sekolah Permata Harapan Secondary is dedicated to empower students through a values-driven education, nurturing each student\'s spiritual, intellectual, and personal growth, inspiring them to face challenges with resilience, purpose, and a commitment to excellence.',
        description: page?.core_values_description || 'Our core values of integrity, excellence, compassion, and service define who we are and guide how we treat one another every day.',
        image: aboutHero3,
      },
    ],
  };

  return (
    <section className="about-us-page">
      <section className="about-hero" id="about-overview">
        <div className="about-hero-media" aria-hidden="true">
          {heroSlides.map((slide, index) => (
            <div
              key={slide}
              className={`about-hero-slide ${index === activeHero ? 'active' : ''}`}
            >
              <img src={slide} alt="About us hero" />
            </div>
          ))}
        </div>
        <div className="about-hero-overlay" aria-hidden="true" />
        <div className="about-hero-content">
          <p className="about-hero-kicker">About Us</p>
          <h2>{page?.hero_title || page?.title || 'About Us'}</h2>
          <p>
            {page?.hero_summary ||
              'Discover our story, values, and the people who shape Permata Harapan School.'}
          </p>
          <div className="about-hero-dots" role="tablist" aria-label="About hero slides">
            {heroSlides.map((slide, index) => (
              <button
                key={slide}
                type="button"
                className={`about-hero-dot ${index === activeHero ? 'active' : ''}`}
                onClick={() => setActiveHero(index)}
                aria-label={`Show slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      <div className={`about-us-layout about-animate ${animateWelcome ? 'animate-in' : ''}`} id="welcome-message">
        <aside className="about-photo-pane" aria-label="Head of School">
          <div className="about-photo-blob" aria-hidden="true" />
          <div className="about-photo-card about-animate-up">
            <div className="about-head-photo-frame">
              <img src={headOfSchoolPhoto} alt="Head of School" className="about-head-photo" />
            </div>
          </div>
          <div className="about-photo-caption">
            <p className="about-side-role">{profile.role}</p>
            <p className="about-side-quote">{profile.name}</p>
            <p className="about-side-theme">{profile.themeLine}</p>
          </div>
        </aside>

        <article className="about-message-pane about-animate-up">
          <div className="about-message-inner">
            <p className="about-message-kicker">Welcome Message</p>
            <h2 className="about-message-title">From the Principal / Head of School</h2>
            <p className="about-message-subtitle">A message of vision, values, and partnership</p>
            <p className="about-message-intro">{profile.opening}</p>
            <p>{profile.paragraphOne}</p>
            <p>{profile.paragraphTwo}</p>
            <p>{profile.closing}</p>
            <div className="about-message-sign">
              <strong>{profile.name}, {profile.credentials}</strong>
              <span>{profile.role} • Permata Harapan School</span>
            </div>
          </div>
        </article>
      </div>

      <div className="about-us-values-shell" id="vision-mission-values">
        <div className="about-us-values-header">
          <p className="about-values-kicker">{valuesSection.kicker}</p>
          <h3>{valuesSection.title}</h3>
          <p>{valuesSection.intro}</p>
        </div>

        <div className="about-values-grid">
          {valuesSection.cards.map((card) => (
            <div
              className={`value-card-new ${selectedValue?.key === card.key ? 'expanded' : ''}`}
              key={card.key}
              onClick={() => setSelectedValue(selectedValue?.key === card.key ? null : card)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSelectedValue(selectedValue?.key === card.key ? null : card);
                }
              }}
            >
              {selectedValue?.key !== card.key && (
                <>
                  <div className="value-card-new-image">
                    <img src={card.image} alt={card.label} />
                    <div className="value-card-new-image-overlay">
                      <h4>{card.label}</h4>
                    </div>
                  </div>
                  <div className="value-card-new-content">
                    <h3>{card.label}</h3>
                  </div>
                </>
              )}

              {selectedValue?.key === card.key && (
                <div className="value-card-expanded-content">
                  <h4>{card.title}</h4>
                  <p>{card.description}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="about-philosophy-shell" id="school-philosophy">
        <div className="about-philosophy-header">
          <p className="about-values-kicker">School Philosophy</p>
          <h3>Our Guiding Principles</h3>
          <p>
            Discover the core values and vision that define Permata Harapan School
          </p>
        </div>
        <div className="about-philosophy-modern">
          <div className="about-philosophy-tabs" role="tablist" aria-label="School philosophy">
            {philosophyItems.map((item, index) => (
              <button
                key={item.key}
                id={`philosophy-tab-${item.key}`}
                type="button"
                className={`about-philosophy-tab ${index === activePhilosophy ? 'active' : ''}`}
                onClick={() => {
                  setActivePhilosophy(index);
                  setIsAutoPlay(false);
                }}
                role="tab"
                aria-selected={index === activePhilosophy}
                aria-controls="philosophy-panel"
              >
                <span className="about-philosophy-tab-index">{item.index}</span>
                <span className="about-philosophy-tab-label">{item.label}</span>
                <span className="about-philosophy-tab-title">{item.shortTitle}</span>
              </button>
            ))}
          </div>

          <div className="about-philosophy-panel-wrapper">
            <button
              type="button"
              className="about-philosophy-nav about-philosophy-nav-prev"
              onClick={goToPreviousPhilosophy}
              aria-label="Show previous philosophy"
            >
              ‹
            </button>
            <article
              className="about-philosophy-panel"
              id="philosophy-panel"
              role="tabpanel"
              aria-labelledby={`philosophy-tab-${philosophyItems[activePhilosophy].key}`}
              key={philosophyItems[activePhilosophy].key}
              onTouchStart={handlePhilosophyTouchStart}
              onTouchEnd={handlePhilosophyTouchEnd}
            >
              <div className="about-philosophy-panel-media" aria-hidden="true">
                <img
                  src={philosophyItems[activePhilosophy].image}
                  alt={philosophyItems[activePhilosophy].label}
                />
              </div>
              <div className="about-philosophy-panel-body">
                <div className="about-philosophy-panel-top">
                  <span className="about-philosophy-panel-badge">
                    {philosophyItems[activePhilosophy].label}
                  </span>
                  <span className="about-philosophy-panel-mark">
                    {philosophyItems[activePhilosophy].index}
                  </span>
                </div>
                <h4>{philosophyItems[activePhilosophy].title}</h4>
                {philosophyItems[activePhilosophy].description && (
                  <p className="about-philosophy-panel-description">
                    {philosophyItems[activePhilosophy].description}
                  </p>
                )}
                {philosophyItems[activePhilosophy].points?.length > 0 && (
                  <div className="about-philosophy-panel-points">
                    {philosophyItems[activePhilosophy].points.map((point, idx) => (
                      <div className="about-philosophy-point" key={point.title} style={{ animationDelay: `${idx * 80}ms` }}>
                        <h5>{point.title}</h5>
                        <p>{point.description}</p>
                      </div>
                    ))}
                  </div>
                )}
                {philosophyItems[activePhilosophy].quote && (
                  <p className="about-philosophy-panel-quote">
                    "{philosophyItems[activePhilosophy].quote}"
                  </p>
                )}
              </div>
            </article>
            <button
              type="button"
              className="about-philosophy-nav about-philosophy-nav-next"
              onClick={goToNextPhilosophy}
              aria-label="Show next philosophy"
            >
              ›
            </button>
          </div>
        </div>
      </div>

      <div className="about-history-shell" id="our-history">
        <div className="about-history-header">
          <p className="about-values-kicker">History</p>
          <h3>Our Beginning</h3>
          <p>
            A brief story of how and why Permata Harapan School was founded.
          </p>
        </div>
        <div className="history-modern">
          <div
            className="history-modern-stage"
            id="history-panel"
            role="tabpanel"
            style={{ backgroundImage: `url(${historyTimeline[activeHistoryIndex]?.image})` }}
          >
            <div className="history-modern-overlay" aria-hidden="true" />
            <div className="history-modern-content">
              <div className="history-modern-top">
                <span className="history-modern-index">0{activeHistoryIndex + 1}</span>
                <span className="history-modern-year">{historyTimeline[activeHistoryIndex]?.year}</span>
              </div>
              <h4>{historyTimeline[activeHistoryIndex]?.title}</h4>
              <p>{historyTimeline[activeHistoryIndex]?.description}</p>
            </div>
          </div>

          <div className="history-modern-list" role="tablist" aria-label="History timeline">
            {historyTimeline.map((item, index) => (
              <button
                key={item.year}
                type="button"
                className={`history-modern-item ${index === activeHistoryIndex ? 'active' : ''}`}
                onClick={() => setActiveHistoryIndex(index)}
                role="tab"
                aria-selected={index === activeHistoryIndex}
                aria-controls="history-panel"
              >
                <span className="history-modern-item-year">{item.year}</span>
                <span className="history-modern-item-title">{item.title}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="history-modern-footer">
          <article className="history-modern-story">
            <p className="history-story-kicker">Why We Started</p>
            <h4>A promise to families, built for the next generation.</h4>
            <p className="history-modern-story-text">
              We began with a simple mission: create a safe, joyful place where learning feels meaningful and
              character grows alongside achievement.
            </p>
            <div className="history-modern-story-highlights">
              <span>Faith-led foundation</span>
              <span>Future-ready learning</span>
              <span>Whole-child care</span>
              <span>Family partnership</span>
            </div>
            <p className="history-modern-story-note">Growing with our community since 2012.</p>
          </article>

          <div className="history-modern-established">
            <div className="history-established-top">
              <div>
                <p className="history-established-kicker">Established</p>
                <h4>{historyTimeline[0]?.year || '2012'}</h4>
              </div>
              <span className="history-established-badge">Since</span>
            </div>
            <p className="history-established-summary">
              Built from a parent-led vision to raise confident learners with character, curiosity, and joyful service.
            </p>
            <div className="history-established-tags">
              <span>Primary</span>
              <span>Values-Driven</span>
              <span>Community</span>
              <span>Faith-Centered</span>
            </div>
            <div className="history-established-facts">
              <div className="history-established-fact">
                <span className="history-fact-label">Focus</span>
                <strong>Faith-led growth</strong>
              </div>
              <div className="history-established-fact">
                <span className="history-fact-label">Approach</span>
                <strong>Whole-child learning</strong>
              </div>
              <div className="history-established-fact">
                <span className="history-fact-label">Impact</span>
                <strong>Community-first</strong>
              </div>
            </div>
            
            <div className="history-modern-metrics">
              <div className="history-modern-metric">
                <span>2</span>
                <small>Campuses</small>
              </div>
              <div className="history-modern-metric">
                <span>1200+</span>
                <small>Alumni</small>
              </div>
              <div className="history-modern-metric">
                <span>12+</span>
                <small>Programs</small>
              </div>
            </div>
            
            <div className="history-established-cta">
              <span>Built on faith, growing with families.</span>
            </div>
          </div>
        </div>
      </div>

      <div className="about-leadership-shell" id="school-leadership">
        <div className="about-leadership-header">
          <p className="about-leadership-kicker">School Leadership</p>
          <h3>Profiles of the principal and key staff</h3>
          <p>
            Meet the leaders who shape our culture, strengthen academics, and support every student journey.
          </p>
        </div>

        <div className="about-leadership-grid">
          {leadershipProfiles.map((leader) => (
            <article className="leadership-card" key={leader.name}>
              <div className="leadership-photo">
                <img src={leader.photo || headOfSchoolPhoto} alt={leader.name} />
              </div>
              <div className="leadership-body">
                <p className="leadership-role">{leader.role}</p>
                <h4>{leader.name}</h4>
                <p>{leader.bio}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

export default AboutUsPage;
